import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import InquiryDialog from "@/components/InquiryDialog";
import { ArrowRight, MapPin, Calendar, Users, Search, Package as PkgIcon, Car, Check } from "lucide-react";

const TABS = [
  { id: "one_way", label: "One Way" },
  { id: "round_trip", label: "Round Trip" },
  { id: "local", label: "Local" },
];

function ResultsDialog({ open, onOpenChange, kind, form, destinations, vehicles, packages }) {
  const nav = useNavigate();
  const results = useMemo(() => {
    if (!open) return [];
    if (kind === "local") {
      // Local tab → return packages matching location
      const target = (form.location || "").toLowerCase();
      const list = packages.filter((p) => !target || p.destination?.toLowerCase().includes(target) || p.name?.toLowerCase().includes(target));
      const withId = form.package_id ? list.filter((p) => p.id === form.package_id) : list;
      return withId.map((p) => ({ type: "package", data: p }));
    }
    // One Way / Round Trip → vehicles filtered by min capacity
    const min = Number(form.travellers || 0);
    const list = vehicles
      .filter((v) => (min ? Number(v.seats || 0) >= min : true))
      .sort((a, b) => (a.seats || 0) - (b.seats || 0));
    return list.map((v) => ({ type: "vehicle", data: v }));
  }, [open, kind, form, packages, vehicles]);

  const tripLabel = kind === "local"
    ? `${form.location || "Local package"}${form.travel_date ? " · " + form.travel_date : ""}`
    : `${form.from || "?"} → ${form.to || "?"}${form.travel_date ? " · " + form.travel_date : ""}${kind === "round_trip" && form.return_date ? " · Return " + form.return_date : ""}`;

  const prefillMessage = (result) => {
    if (result.type === "package") {
      return `Hello! I'd like to book the ${result.data.name} package.\n\nDates: ${form.travel_date || "Flexible"}\nPassengers: ${form.travellers || "—"}`;
    }
    return `Hello! I'd like to book a ${result.data.name}.\n\nTrip: ${tripLabel}\nPassengers: ${form.travellers || "—"}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="search-results-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Search results</DialogTitle>
          <DialogDescription className="text-sm text-slate-500 mt-1">{tripLabel}</DialogDescription>
        </DialogHeader>

        {results.length === 0 ? (
          <div className="py-12 text-center text-slate-500">
            <div className="font-medium">No trips found for your selected criteria.</div>
            <div className="text-sm mt-1">Please try another date, destination or package.</div>
          </div>
        ) : (
          <div className="space-y-4 mt-4">
            {results.map((r) => r.type === "vehicle" ? (
              <div key={r.data.id} className="rounded-xl border border-slate-200 p-4 flex flex-col sm:flex-row gap-4" data-testid={`result-vehicle-${r.data.slug}`}>
                <img src={r.data.image_url} alt={r.data.name} className="w-full sm:w-40 h-28 object-cover rounded-lg" />
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-display text-lg text-slate-950">{r.data.name}</div>
                      <div className="text-sm text-slate-500 mt-1 flex flex-wrap items-center gap-3">
                        <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {r.data.seats} seats</span>
                        {r.data.ac && <span className="inline-flex items-center gap-1"><Check className="w-3.5 h-3.5 text-emerald-600" /> AC</span>}
                        <span className="text-emerald-700 font-medium">Available</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-slate-500">From</div>
                      <div className="font-display text-lg text-brand-navy-900 whitespace-nowrap">{r.data.price_hint || "On enquiry"}</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <InquiryDialog defaultMessage={prefillMessage(r)} source="booking_search" sourceRef={r.data.slug} triggerTestId={`result-vehicle-select-${r.data.slug}`}>
                      <Button className="rounded-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid={`result-vehicle-select-${r.data.slug}`}>
                        Select & Continue <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </InquiryDialog>
                    <Button variant="outline" className="rounded-full" onClick={() => { onOpenChange(false); nav(`/transportation/${r.data.slug}`); }}>View details</Button>
                  </div>
                </div>
              </div>
            ) : (
              <div key={r.data.id} className="rounded-xl border border-slate-200 p-4 flex flex-col sm:flex-row gap-4" data-testid={`result-package-${r.data.slug}`}>
                <img src={r.data.banner_url} alt={r.data.name} className="w-full sm:w-40 h-28 object-cover rounded-lg" />
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-xs uppercase tracking-[0.2em] text-amber-600">{r.data.destination} · {r.data.duration_days}D / {r.data.duration_nights}N</div>
                      <div className="font-display text-lg text-slate-950 mt-1">{r.data.name}</div>
                      <p className="text-sm text-slate-600 mt-1 line-clamp-2">{r.data.overview}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-slate-500">Starting from</div>
                      <div className="font-display text-lg text-brand-navy-900 whitespace-nowrap">₹{r.data.price_from?.toLocaleString?.("en-IN")}</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <InquiryDialog defaultMessage={prefillMessage(r)} source="booking_search" sourceRef={r.data.slug} triggerTestId={`result-package-select-${r.data.slug}`}>
                      <Button className="rounded-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid={`result-package-select-${r.data.slug}`}>
                        Select & Continue <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </InquiryDialog>
                    <Button variant="outline" className="rounded-full" onClick={() => { onOpenChange(false); nav(`/packages/${r.data.slug}`); }}>View package</Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function BookingSearch({ variant = "desktop" }) {
  const [tab, setTab] = useState("one_way");
  const [form, setForm] = useState({});
  const [open, setOpen] = useState(false);

  const { data: destinations = [] } = useQuery({ queryKey: ["dest-active"], queryFn: () => api.get("/destinations", { params: { is_active: true, limit: 100 } }).then((r) => r.data.items) });
  const { data: vehicles = [] } = useQuery({ queryKey: ["veh-active"], queryFn: () => api.get("/vehicles", { params: { is_active: true, limit: 100 } }).then((r) => r.data.items) });
  const { data: packages = [] } = useQuery({ queryKey: ["pkg-active"], queryFn: () => api.get("/packages", { params: { is_active: true, limit: 100 } }).then((r) => r.data.items) });

  // Pickup/drop options: Visakhapatnam (default origin) + all destinations
  const locationOptions = useMemo(() => {
    const base = ["Visakhapatnam", "Vizag City", "Vizag Airport", "Vizag Railway Station"];
    const cities = Array.from(new Set(destinations.map((d) => d.name).filter(Boolean)));
    return Array.from(new Set([...base, ...cities]));
  }, [destinations]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (kind === "tour") return true; // list always
    if (kind === "airport") {
      if (!form.from) { toast.error("Please choose pickup location"); return false; }
      if (!form.to) { toast.error("Please choose destination"); return false; }
      if (!form.travel_date) { toast.error("Please choose a travel date"); return false; }
      return true;
    }
    if (!form.from) { toast.error("Please choose pickup location"); return false; }
    if (!form.to) { toast.error("Please choose destination"); return false; }
    if (form.from === form.to) { toast.error("From & To must be different"); return false; }
    if (!form.travel_date) { toast.error("Please choose a travel date"); return false; }
    return true;
  };

  const doSearch = (e) => {
    e?.preventDefault();
    const kind = tab;
    const validatorOk = (() => {
      if (kind === "local") return true;
      if (!form.from) { toast.error("Please choose pickup location"); return false; }
      if (!form.to) { toast.error("Please choose destination"); return false; }
      if (form.from === form.to) { toast.error("From & To must be different"); return false; }
      if (!form.travel_date) { toast.error("Please choose a travel date"); return false; }
      if (kind === "round_trip" && !form.return_date) { toast.error("Please choose a return date"); return false; }
      return true;
    })();
    if (!validatorOk) return;
    setOpen(true);
  };

  // Field UI helpers
  const Select = ({ id, value, onChange, options, placeholder, icon: Icon, testId }) => (
    <div className="relative">
      {Icon && <Icon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />}
      <select
        id={id}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className={`w-full h-12 border rounded-lg pr-3 bg-white border-slate-300 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-400 ${Icon ? "pl-9" : "pl-3"}`}
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (typeof o === "string" ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>))}
      </select>
    </div>
  );

  const DateInput = ({ id, value, onChange, testId }) => (
    <div className="relative">
      <Calendar className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
      <Input id={id} type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-12 pl-9" data-testid={testId} />
    </div>
  );

  const Field = ({ label, children }) => (
    <div className="flex flex-col gap-1.5">
      <Label className="text-[11px] uppercase tracking-[0.16em] text-slate-500">{label}</Label>
      {children}
    </div>
  );

  return (
    <>
      <div
        className="bg-white rounded-2xl shadow-xl border border-slate-200 p-4 sm:p-5 lg:p-6"
        data-testid={`booking-search-${variant}`}
      >
        <div role="tablist" className="flex flex-wrap gap-1 border-b border-slate-200 -mx-1 mb-4 sm:mb-5">
          {TABS.map((t) => (
            <button
              key={t.id}
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => { setTab(t.id); setForm({}); }}
              data-testid={`booking-tab-${t.id}`}
              className={`px-4 py-2 text-sm font-semibold rounded-t-md transition-colors ${
                tab === t.id
                  ? "text-brand-navy-900 border-b-2 border-brand-navy-900"
                  : "text-slate-500 hover:text-slate-950"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <form onSubmit={doSearch} className={`grid gap-3 ${tab === "local" ? "grid-cols-1 lg:grid-cols-5" : tab === "round_trip" ? "grid-cols-1 lg:grid-cols-6" : "grid-cols-1 lg:grid-cols-5"}`}>
          {tab !== "local" && (
            <>
              <Field label="From">
                <Select value={form.from} onChange={(v) => setField("from", v)} options={locationOptions} placeholder="Select Location" icon={MapPin} testId="bs-from" />
              </Field>
              <Field label="To">
                <Select value={form.to} onChange={(v) => setField("to", v)} options={locationOptions} placeholder="Select Location" icon={MapPin} testId="bs-to" />
              </Field>
              <Field label={tab === "round_trip" ? "Departure Date" : "Date"}>
                <DateInput value={form.travel_date} onChange={(v) => setField("travel_date", v)} testId="bs-date" />
              </Field>
              {tab === "round_trip" && (
                <Field label="Return Date">
                  <DateInput value={form.return_date} onChange={(v) => setField("return_date", v)} testId="bs-return" />
                </Field>
              )}
              <Field label="Passengers">
                <Select
                  value={form.travellers}
                  onChange={(v) => setField("travellers", v)}
                  options={["1", "2", "3", "4", "5", "6", "7", "8", "10", "12", "15", "20", "25", "30", "45"]}
                  placeholder="Any"
                  icon={Users}
                  testId="bs-pax"
                />
              </Field>
            </>
          )}
          {tab === "local" && (
            <>
              <Field label="Location">
                <Select value={form.location} onChange={(v) => setField("location", v)} options={locationOptions} placeholder="Choose city / area" icon={MapPin} testId="bs-location" />
              </Field>
              <Field label="Package">
                <Select
                  value={form.package_id}
                  onChange={(v) => setField("package_id", v)}
                  options={packages.map((p) => ({ value: p.id, label: `${p.name} · ${p.duration_days}D` }))}
                  placeholder="Any package"
                  icon={PkgIcon}
                  testId="bs-package"
                />
              </Field>
              <Field label="Travel Date">
                <DateInput value={form.travel_date} onChange={(v) => setField("travel_date", v)} testId="bs-ldate" />
              </Field>
              <Field label="Passengers">
                <Select
                  value={form.travellers}
                  onChange={(v) => setField("travellers", v)}
                  options={["1", "2", "3", "4", "5", "6", "7", "8", "10", "12", "15", "20", "30+"]}
                  placeholder="Number"
                  icon={Users}
                  testId="bs-pax"
                />
              </Field>
            </>
          )}
          <div className="flex items-end">
            <Button type="submit" className="w-full h-12 rounded-lg bg-brand-navy-800 hover:bg-brand-navy-700 text-white font-semibold shadow" data-testid="bs-search-btn">
              <Search className="w-4 h-4 mr-2" /> Search Now
            </Button>
          </div>
        </form>
      </div>

      <ResultsDialog
        open={open}
        onOpenChange={setOpen}
        kind={tab}
        form={form}
        destinations={destinations}
        vehicles={vehicles}
        packages={packages}
      />
    </>
  );
}